import DOMPurify from 'dompurify';

// Enhanced input sanitization utilities
export class Sanitizer {
  // SQL/NoSQL injection prevention with enhanced patterns
  static sanitizeQuery(input: string): string {
    if (!input || typeof input !== 'string') return '';
    
    // Remove dangerous SQL/NoSQL patterns
    const dangerousPatterns = [
      /('|(\-\-)|(;)|(\|)|(\*)|(%))/gi,
      /(union|select|insert|update|delete|drop|create|alter|exec|execute)/gi,
      /(script|javascript|vbscript|onload|onerror|onclick)/gi,
      /(\$where|\$ne|\$gt|\$lt|\$regex|\$or|\$and)/gi
    ];
    
    let sanitized = input;
    dangerousPatterns.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });
    
    return sanitized.trim().substring(0, 1000);
  }

  // Enhanced XSS prevention
  static sanitizeHtml(input: string): string {
    if (!input || typeof input !== 'string') return '';
    
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;')
      .replace(/\(/g, '&#40;')
      .replace(/\)/g, '&#41;')
      .replace(/javascript:/gi, '')
      .replace(/data:/gi, '')
      .replace(/vbscript:/gi, '')
      .replace(/on\w+=/gi, '');
  }

  static sanitizeRichText(input: string): string {
    if (!input || typeof input !== 'string') return '';

    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li',
        'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'span', 'div', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'src', 'alt', 'title'],
      ALLOW_DATA_ATTR: false
    });
  }

  // Enhanced log injection prevention
  static sanitizeLog(input: string): string {
    if (!input || typeof input !== 'string') return '';
    
    return input
      .replace(/[\r\n\t\x00-\x1f\x7f-\x9f]/g, ' ')
      .replace(/\\[rnt]/g, ' ')
      .replace(/%0[ad]/gi, ' ')
      .replace(/%2[ef]/gi, ' ')
      .trim()
      .substring(0, 500);
  }

  // Enhanced topic validation
  static validateTopic(topic: string): boolean {
    if (!topic || typeof topic !== 'string') return false;
    if (topic.length < 1 || topic.length > 100) return false;
    
    // Allow Turkish characters, alphanumeric, spaces, hyphens, underscores, dots
    return /^[a-zA-ZğüşıöçĞÜŞİÖÇ0-9\s\-_.]+$/.test(topic);
  }

  // Email validation
  static validateEmail(email: string): boolean {
    if (!email || typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  }

  // Phone number validation (Turkish format)
  static validatePhone(phone: string): boolean {
    if (!phone || typeof phone !== 'string') return false;
    const phoneRegex = /^(\+90|0)?[5][0-9]{9}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  }

  // Numeric input validation
  static validateNumeric(input: string | number, min?: number, max?: number): boolean {
    const num = typeof input === 'string' ? parseFloat(input) : input;
    if (isNaN(num)) return false;
    if (min !== undefined && num < min) return false;
    if (max !== undefined && num > max) return false;
    return true;
  }

  // File name sanitization
  static sanitizeFileName(fileName: string): string {
    if (!fileName || typeof fileName !== 'string') return '';
    
    return fileName
      .replace(/[^a-zA-Z0-9\-_.]/g, '_')
      .replace(/_{2,}/g, '_')
      .substring(0, 255);
  }

  // URL validation
  static validateUrl(url: string): boolean {
    if (!url || typeof url !== 'string') return false;
    
    try {
      const urlObj = new URL(url);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  }

  // Deep sanitize object (recursive)
  static sanitizeObject(
    obj: any,
    options: { richTextKeys?: string[] } = {},
    currentKey?: string
  ): any {
    if (obj === null || obj === undefined) return obj;
    
    if (typeof obj === 'string') {
      if (currentKey && (options.richTextKeys || []).includes(currentKey)) {
        return this.sanitizeRichText(obj);
      }

      return this.sanitizeHtml(obj);
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item, options, currentKey));
    }
    
    if (typeof obj === 'object') {
      const sanitized: any = {};
      for (const [key, value] of Object.entries(obj)) {
        const sanitizedKey = this.sanitizeHtml(key);
        sanitized[sanitizedKey] = this.sanitizeObject(value, options, key);
      }
      return sanitized;
    }
    
    return obj;
  }
}

// Rate limiting helper
export class RateLimiter {
  private static requests = new Map<string, number[]>();
  
  static isAllowed(identifier: string, maxRequests: number = 100, windowMs: number = 60000): boolean {
    const now = Date.now();
    const windowStart = now - windowMs;
    
    if (!this.requests.has(identifier)) {
      this.requests.set(identifier, []);
    }
    
    const userRequests = this.requests.get(identifier)!;
    
    // Remove old requests outside the window
    const validRequests = userRequests.filter(time => time > windowStart);
    
    if (validRequests.length >= maxRequests) {
      return false;
    }
    
    validRequests.push(now);
    this.requests.set(identifier, validRequests);
    
    return true;
  }
  
  static reset(identifier: string): void {
    this.requests.delete(identifier);
  }
}
